import {
  ExcelSpreadsheetTemplateTabInput,
  WordDocumentTemplateSectionInput,
  type VisibilityConfigInput
} from '~/api/graphql';

/**
 * Decode the visibility FormData field set by client-side dialogs that submit
 * via fetcher.submit + multipart/form-data. The dialog encodes the
 * VisibilityConfigInput as JSON. Returns undefined for an absent or
 * malformed field, leaving the mutation's visibility behavior to the server.
 */
export const parseVisibilityFormField = (
  raw: FormDataEntryValue | null
): VisibilityConfigInput | undefined => {
  if (typeof raw !== 'string' || !raw.trim()) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as VisibilityConfigInput;
    if (!parsed || typeof parsed !== 'object' || !parsed.level) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

/** Read an optional free-text form field, omitting it from the mutation input
 *  entirely when the user left it blank. */
export const parseOptionalTrimmedFormField = (
  raw: FormDataEntryValue | null
): string | undefined => {
  if (typeof raw !== 'string' || !raw.trim()) {
    return undefined;
  }
  return raw.trim();
};

export const parseHeaderFooterFromFormData = (formData: FormData) => {
  const parseSlot = (prefix: string) => {
    const textRaw = formData.get(`${prefix}.text`) as string | null;
    const fileIdRaw = formData.get(`${prefix}.image.fileId`) as string | null;
    const uploadRaw = formData.get(`${prefix}.image.upload`);
    const maxHeightRaw = formData.get(`${prefix}.image.maxHeightPt`) as string | null;
    const paddingPtRaw = formData.get(`${prefix}.image.paddingPt`) as string | null;
    const innerGapPtRaw = formData.get(`${prefix}.image.innerGapPt`) as string | null;

    const slot: {
      text?: string | null;
      image?: {
        fileId?: string | null;
        upload?: File | null;
        maxHeightPt?: number | null;
        paddingPt?: number | null;
        innerGapPt?: number | null;
      } | null;
    } = {};

    const text = textRaw !== null ? textRaw.trim() : null;
    if (textRaw !== null) {
      slot.text = text === '' ? null : text;
    }

    const upload = uploadRaw instanceof File ? uploadRaw : null;
    const trimmedFileId = fileIdRaw?.trim() ?? '';
    // Only create an image payload when there is an actual file reference — numeric
    // spacing fields alone (no file) do not constitute a valid image slot and would
    // produce a malformed mutation input.
    const hasImage = upload || trimmedFileId !== '';
    if (hasImage) {
      const image: {
        fileId?: string | null;
        upload?: File | null;
        maxHeightPt?: number | null;
        paddingPt?: number | null;
        innerGapPt?: number | null;
      } = {};
      if (trimmedFileId !== '') {
        image.fileId = trimmedFileId;
      }
      if (upload) {
        image.upload = upload;
      }

      if (maxHeightRaw !== null && maxHeightRaw.trim() !== '') {
        const parsed = Number(maxHeightRaw);
        image.maxHeightPt = Number.isNaN(parsed) ? null : parsed;
      }

      if (paddingPtRaw !== null && paddingPtRaw.trim() !== '') {
        const parsed = Number(paddingPtRaw);
        image.paddingPt = Number.isNaN(parsed) ? null : parsed;
      }

      if (innerGapPtRaw !== null && innerGapPtRaw.trim() !== '') {
        const parsed = Number(innerGapPtRaw);
        image.innerGapPt = Number.isNaN(parsed) ? null : parsed;
      }

      slot.image = image;
    }

    if (slot.text === undefined && slot.image === undefined) {
      return null;
    }

    // Only return null if the text field was NOT present in FormData (textRaw === null)
    // If textRaw !== null, it means the user explicitly set/cleared the text field
    if (textRaw === null && (!slot.image || (!slot.image.fileId && !slot.image.upload))) {
      return null;
    }

    return slot;
  };

  const headerLeft = parseSlot('headerFooter.header.left');
  const headerCenter = parseSlot('headerFooter.header.center');
  const headerRight = parseSlot('headerFooter.header.right');
  const footerLeft = parseSlot('headerFooter.footer.left');
  const footerCenter = parseSlot('headerFooter.footer.center');
  const footerRight = parseSlot('headerFooter.footer.right');

  if (
    !headerLeft &&
    !headerCenter &&
    !headerRight &&
    !footerLeft &&
    !footerCenter &&
    !footerRight
  ) {
    return null;
  }

  return {
    header: {
      left: headerLeft,
      center: headerCenter,
      right: headerRight
    },
    footer: {
      left: footerLeft,
      center: footerCenter,
      right: footerRight
    }
  };
};

export function parseStyleFromFormData(formData: FormData): Record<string, unknown> | undefined {
  const raw = formData.get('style');
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

/**
 * Reconstruct the `sections: WordDocumentTemplateSectionInput[]` payload from
 * FormData posted by the Word template dialog. The dialog encodes section
 * metadata as a JSON blob under the `sections` key and uploads each section's
 * file separately under `sections[<i>].uploadTemplate`.
 *
 * Returns `undefined` for missing/blank fields, invalid JSON, or non-array
 * values. The Word template create and update handlers use `[]` as their
 * fallback; this parser does not decide whether to omit the mutation field.
 */
export const parseWordDocumentTemplateSectionsFromFormData = (
  formData: FormData
): WordDocumentTemplateSectionInput[] | undefined => {
  const raw = formData.get('sections');
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;

  let meta: Array<{
    id?: unknown;
    title?: unknown;
    setupGuidance?: unknown;
    static?: unknown;
    repeatable?: unknown;
    required?: unknown;
    templateFileId?: unknown;
  }>;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    meta = parsed;
  } catch {
    return undefined;
  }

  return meta.map((entry, i) => {
    const upload = formData.get(`sections[${i}].uploadTemplate`);
    const idRaw = typeof entry.id === 'string' ? entry.id : '';
    const templateFileIdRaw = typeof entry.templateFileId === 'string' ? entry.templateFileId : '';
    const setupGuidanceRaw =
      typeof entry.setupGuidance === 'string' ? entry.setupGuidance.trim() : '';
    return {
      // The editor sends the existing section's ID so the backend can match
      // existing sections by identity (preserving extracted fill guidance
      // and fields across reorders). The create dialog omits this field;
      // omit it from the input as well so the server treats the entry as a
      // brand new section.
      id: idRaw.trim() === '' ? undefined : idRaw.trim(),
      title: typeof entry.title === 'string' ? entry.title : '',
      // User-provided guidance fed to the LLM at ingestion time. Blank values
      // are sent as null so the server clears any stored guidance.
      setupGuidance: setupGuidanceRaw === '' ? null : setupGuidanceRaw,
      static: entry.static === true,
      repeatable: entry.repeatable === true,
      // `required` is a non-nullable Boolean! on the input — older payloads
      // (or anything coercible-but-not-`true`) fall back to `true` rather
      // than `false` so we don't silently flip a required section to
      // optional on round-trip through the action.
      required: entry.required === false ? false : true,
      uploadTemplate: upload instanceof File ? upload : null,
      templateFileId: templateFileIdRaw.trim() === '' ? null : templateFileIdRaw.trim()
    };
  });
};

/**
 * Reconstruct the `tabs: ExcelSpreadsheetTemplateTabInput[]` payload from the
 * JSON blob the Excel template editor posts under the `tabs` key. Unlike Word
 * sections, tabs carry no file of their own — the workbook is a single upload
 * and its worksheets are discovered server-side — so this is metadata only.
 *
 * Returns `undefined` (so the mutation omits the field entirely) when no tabs
 * were posted, which the server reads as "leave the stored tabs untouched".
 */
export const parseExcelSpreadsheetTemplateTabsFromFormData = (
  formData: FormData
): ExcelSpreadsheetTemplateTabInput[] | undefined => {
  const raw = formData.get('tabs');
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;

  let meta: Array<{
    id?: unknown;
    setupGuidance?: unknown;
    static?: unknown;
    required?: unknown;
  }>;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return undefined;
    meta = parsed;
  } catch {
    return undefined;
  }

  return meta.map((entry) => {
    const setupGuidanceRaw =
      typeof entry.setupGuidance === 'string' ? entry.setupGuidance.trim() : '';
    return {
      id: typeof entry.id === 'string' ? entry.id : '',
      // Blank guidance is sent as null so the server clears any stored value.
      setupGuidance: setupGuidanceRaw === '' ? null : setupGuidanceRaw,
      static: entry.static === true,
      // `required` is a non-nullable Boolean! on the input — anything
      // coercible-but-not-`false` falls back to `true` so a round-trip through
      // the action never silently makes a required tab optional.
      required: entry.required === false ? false : true
    };
  });
};

/**
 * Reads an optional string FormData field, preserving present empty strings.
 *
 * Distinguishes:
 * - absent key / non-string (File) → `undefined`
 * - present empty string `""` → `""`
 * - present non-empty string → the string unchanged
 *
 * Use this when callers need to clear a string field by sending `""`, which
 * `value || undefined` would incorrectly convert to omission.
 */
export const readOptionalFormString = (formData: FormData, key: string): string | undefined => {
  const value = formData.get(key);
  return typeof value === 'string' ? value : undefined;
};
